import React, { useState, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { BookOpen, ChevronDown, ChevronRight, Users, Bell, Calendar, Settings, Shield, Search, MessageSquare, Activity, Download, LogIn, Bed, ClipboardList, FileText, ClipboardCheck, HelpCircle, Rocket } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useEntitlements, type PaidModuleKey } from '@/hooks/useEntitlements';
import { useAuth } from '@/contexts/AuthContext';
import { MODULE_MANUAL_MAP, getModuleForGuideSection } from '@/config/moduleManualMap';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import jsPDF from 'jspdf';
import signInPage from '@/assets/sign-in-page.png';
import clientProfileExample from '@/assets/client-profile-example.png';
import addNewClient from '@/assets/add_new_client.png';
import checkInConfirm from '@/assets/check-in-confirm.png';
import checkOutConfirm from '@/assets/check-out-confirm.png';
import staffAddAlert from '@/assets/staff-add-alert.png';
import adminAlertsSection from '@/assets/admin-alerts-section.png';
import adminSystemSettings from '@/assets/admin-system-settings.png';
import addNewStaffMember from '@/assets/add-new-staff-member.png';
import createRoster from '@/assets/create-roster.png';
import createBedGrid from '@/assets/create-bed-grid.png';
import printDailyCensus from '@/assets/print-daily-census.png';
import clientServicesLog from '@/assets/client-services-log.png';
import staffViewClient from '@/assets/staff-view-client.png';
import addClientService from '@/assets/add-client-service.png';
import checkOutScreen2 from '@/assets/check-out-screen-2.png';
import newClientForm2 from '@/assets/new-client-form-2.png';
import clientListTable from '@/assets/client-list-table.png';
import adminStaff from '@/assets/admin-staff.png';

interface UserGuideProps {
  compact?: boolean;
}


const UserGuide = ({ compact = false }: UserGuideProps) => {
  const [openSections, setOpenSections] = useState<string[]>(['overview']);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [showAllModules, setShowAllModules] = useState(false);
  const printRef = useRef<HTMLDivElement>(null);
  const { hasModule } = useEntitlements();
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'super_admin';

  const toggleSection = (sectionId: string) => {
    setOpenSections(prev =>
      prev.includes(sectionId) ? prev.filter(id => id !== sectionId) : [...prev, sectionId]
    );
  };

  const getScreenshotSrc = (screenshot: string) => {
    switch (screenshot) {
      case 'login': return signInPage;
      case 'client-profile': return clientProfileExample;
      case 'add-client': return addNewClient;
      case 'check-in': return checkInConfirm;
      case 'check-out': return checkOutConfirm;
      case 'add-alert': return staffAddAlert;
      case 'admin-alerts': return adminAlertsSection;
      case 'admin-settings': return adminSystemSettings;
      case 'staff-management': return addNewStaffMember;
      case 'roster-management': return createRoster;
      case 'bed-configuration': return createBedGrid;
      case 'print-census': return printDailyCensus;
      case 'client-services': return clientServicesLog;
      case 'staff-client-view': return staffViewClient;
      case 'add-service': return addClientService;
      case 'check-out-screen': return checkOutScreen2;
      case 'new-client-intake': return newClientForm2;
      case 'client-list-table': return clientListTable;
      case 'admin-staff': return adminStaff;
      default: return null;
    }
  };

  const guideContent = [
    {
      id: 'quick-start',
      title: 'Pilot Quick Start',
      icon: Rocket,
      badge: 'Start Here',
      content: [
        {
          title: 'Get productive in 10 minutes',
          description: 'This is the shortest path through Cimfoni. Each step links to a full section below.',
          steps: [
            '1. **Sign in** with the credentials your admin sent you (change your temporary password on first login).',
            '2. **Intake a client** from the Clients tab — First/Last Name, Age, and a bed.',
            '3. **Assign a bed** during intake, or from the client\'s profile.',
            '4. **Check in** the client from the roster with the Check In action.',
            '5. **Log a service** (meal, laundry, transit) from the client\'s row.',
            '6. **Raise or acknowledge an alert** from the Alerts panel.',
            '7. **End of shift** — open the Shift Report, review, and submit.'
          ],
          callout: { type: 'tip', text: 'Press Cmd+K (or Ctrl+K) anywhere in the app to jump to any client, staff member, or screen.' }
        }
      ]
    },
    {
      id: 'account',
      title: 'Signing In & Your Account',
      icon: LogIn,
      badge: 'Essential',
      content: [
        {
          title: 'First login',
          description: 'Your administrator creates your account and gives you a temporary password.',
          steps: [
            '1. Open the app in Chrome, Firefox, Safari, or Edge.',
            '2. Enter your work email and the temporary password.',
            '3. You will be prompted to set a new password that meets complexity rules.',
            '4. Admins and managers are prompted to enroll TOTP (multi-factor authentication).'
          ],
          callout: { type: 'note', text: 'Sessions expire after 30 minutes of inactivity. You will get a warning before you are signed out.' },
          screenshot: 'login'
        },
        {
          title: 'Forgot password',
          description: 'You can reset your own password without waiting for an admin.',
          steps: [
            '1. On the sign-in screen, click "Forgot password".',
            '2. Enter your work email.',
            '3. Open the email we send and click the reset link.',
            '4. Set a new password.'
          ]
        },
        {
          title: 'Your profile',
          description: 'Open the header menu and choose "My Profile" to see your role, assigned facilities, and notification preferences.',
          callout: { type: 'tip', text: 'Set your notification preferences (in-app, email, SMS) and quiet hours so you only get pinged when it matters.' }
        }
      ]
    },
    {
      id: 'clients',
      title: 'Clients',
      icon: Users,
      badge: 'Core',
      content: [
        {
          title: 'Intake a new client',
          description: 'Every client starts with intake. Cimfoni checks for duplicates before creating the record.',
          steps: [
            '1. Open the Clients tab and click "New Client".',
            '2. Enter First Name, Last Name, and Age (required).',
            '3. Assign a bed if one is available. If not, save the client as Unbedded or add them to the Waitlist.',
            '4. Confirm consent.',
            '5. Save.'
          ],
          callout: { type: 'note', text: 'Sensitive identity fields (name, DOB) can only be edited by admin-tier roles.' },
          screenshot: 'new-client-intake'
        },
        {
          title: 'Client statuses',
          description: 'Cimfoni uses six statuses that reflect the client\'s real position in the shelter:',
          steps: [
            '• **checked-in** — present and in an assigned bed',
            '• **checked-out** — has left the facility',
            '• **unbedded** — registered but no bed assigned',
            '• **not_present** — expected but currently away',
            '• **reserved** — bed is held for them',
            '• **waitlist** — queued for a bed (expires after 24 hours)'
          ]
        },
        {
          title: 'Passes',
          description: 'A pass records a planned absence (medical, work, family) without changing the client\'s status.',
          steps: [
            '• Set pass start and end from the client profile.',
            '• A purple **Pass** badge appears on the roster while a pass is active.',
            '• A client on an active pass cannot be manually unbedded.',
            '• After the pass expires, unbed the client manually for accountability.'
          ]
        },
        {
          title: 'Client profile tabs',
          description: 'Every client profile has five tabs:',
          steps: [
            '• **Details** — identity, contacts, allergies, case number',
            '• **Services** — every service logged for this client',
            '• **Notes** — threaded notes, mentions, attachments',
            '• **History** — activity timeline with field-level diffs',
            '• **Belongings** — property stored and returned'
          ],
          screenshot: 'client-profile'
        },
        {
          title: 'Archive a client',
          description: 'Soft-archive requires a status reason. Restoring resets the client to Unbedded.',
          callout: { type: 'warning', text: 'Never delete a client to "clean up" — archive them. Deletion cascades to notes, service logs, and family check-ins.' }
        }
      ]
    },
    {
      id: 'checkin',
      title: 'Check-In & Check-Out',
      icon: ClipboardCheck,
      badge: 'Daily',
      content: [
        {
          title: 'Check a client in',
          description: 'Two taps with a confirmation dialog. You cannot check a client in without an assigned bed.',
          steps: [
            '1. Find the client on the roster.',
            '2. Click "Check In".',
            '3. Confirm the photo, name, and bed in the dialog.',
            '4. Done — status flips to checked-in and the check-in time is recorded.'
          ],
          screenshot: 'check-in'
        },
        {
          title: 'Check a client out',
          description: 'Same flow in reverse. The bed is released and available to reassign.',
          screenshot: 'check-out'
        },
        {
          title: 'Family check-in',
          description: 'At family shelters, check each family member in and out individually so nightly counts are correct.',
          callout: { type: 'note', text: 'Family Shelter facilities expose a dedicated family check-in dialog with a member list.' }
        }
      ]
    },
    {
      id: 'census',
      title: 'Daily Census & Roster (incl. Rooms & Beds)',
      icon: Calendar,
      badge: 'Core',
      content: [
        {
          title: 'Read the census',
          description: 'The stat cards at the top of the dashboard show live counts. Click any card to see the list of clients behind that number.',
          steps: [
            '• **Checked In** — currently present and bedded',
            '• **Checked Out** — left the facility today',
            '• **Total Clients** — all active clients',
            '• **Unbedded** — registered without a bed',
            '• **On Pass** — clients with an active pass',
            '• **Waitlist** — clients queued for a bed'
          ]
        },
        {
          title: 'Roster columns',
          description: 'Roster order is fixed for muscle memory:',
          steps: [
            'Name · Room · Status (with Pass badge) · Alerts · Case Manager · Last Activity · Actions'
          ],
          callout: { type: 'tip', text: 'Default sort is Last Activity descending so newest intakes appear first. Use Cmd+K to search across the whole system.' },
          screenshot: 'staff-client-view'
        },
        {
          title: 'Rooms & Beds',
          description: 'Rooms are configured per facility. Cimfoni supports Single, Shared (auto a/b sub-rooms), and Communal room types.',
          steps: [
            '1. From the Facilities tab, open a facility and go to Rooms.',
            '2. Add rooms with number, capacity, and type.',
            '3. Mark handicap-accessible rooms with the flag.',
            '4. Assign clients to beds from the roster or the client profile.'
          ],
          callout: { type: 'warning', text: 'Room capacity is enforced by the database — you cannot over-assign a room.' },
          screenshot: 'bed-configuration'
        },
        {
          title: 'Bed count (10 PM / 2 AM)',
          description: 'A structured presence check that produces a signed record.',
          steps: [
            '1. At the scheduled time, open Bed Count from the sidebar.',
            '2. Confirm each bedded client is present.',
            '3. A supervisor must sign off before submission.',
            '4. Discrepancies flow into the Shift Report for follow-up.'
          ]
        },
        {
          title: 'Print the census',
          description: 'Every census view has a Print action that opens a clean, print-optimized popup.',
          screenshot: 'print-census'
        }
      ]
    },
    {
      id: 'alerts',
      title: 'Alerts & @Mentions',
      icon: Bell,
      badge: 'Core',
      content: [
        {
          title: 'Create an alert',
          description: 'Alerts follow a Create → Notify → Resolve lifecycle. Pick the right category so the right people see it.',
          steps: [
            '1. Click "New Alert" from the Alerts panel.',
            '2. Choose category — **Staff**, **System**, or **Building**.',
            '3. Set priority — critical, warning, or info.',
            '4. Write a short title and description.',
            '5. Submit. Notifications go out based on each recipient\'s preferences and quiet hours.'
          ],
          screenshot: 'add-alert'
        },
        {
          title: 'Acknowledge an alert',
          description: 'Open the alert and click Acknowledge. Your acknowledgment is recorded with a timestamp.',
          screenshot: 'admin-alerts'
        },
        {
          title: '@Mention a teammate',
          description: 'Type @ in any note or alert to mention a staff member or case manager. They get an SMS/email based on their preferences and the mention is logged.',
          callout: { type: 'tip', text: 'Mentions are the fastest way to hand off a task without leaving the note you\'re writing.' }
        }
      ]
    },
    {
      id: 'services',
      title: 'Services & Meals',
      icon: ClipboardList,
      badge: 'Daily',
      content: [
        {
          title: 'Log a service',
          description: 'From the roster row or the client profile, click the services icon to log meals, laundry, transit, or a custom service with a quantity and note.',
          screenshot: 'add-service'
        },
        {
          title: 'Meals lifecycle',
          description: 'Meals are tracked at the facility level for accountability:',
          steps: [
            '• **Received** — what came in',
            '• **Distributed** — what was served',
            '• **Discarded** — what was wasted'
          ],
          callout: { type: 'note', text: 'The Shift Report has a dedicated Meals & Beverages section that summarizes breakfast, lunch, dinner, milk, and juice.' }
        },
        {
          title: 'Weekly menu',
          description: 'Upload the weekly menu (image or PDF) from the Services tab so staff can reference it during meal service.'
        }
      ]
    },
    {
      id: 'notes',
      title: 'Notes & Documents',
      icon: FileText,
      badge: 'Core',
      content: [
        {
          title: 'Write a note',
          description: 'Notes support types (general, progress, observation, incident), threaded replies, attachments, and @mentions.',
          steps: [
            '• Mark a note **private** to limit visibility to admin-tier roles.',
            '• Attach files up to 10 MB.',
            '• @mention teammates to notify them.'
          ]
        },
        {
          title: 'Documents',
          description: 'The document library lives per facility. Admins upload; everyone reads and acknowledges.',
          steps: [
            '• Categories and tags make documents easy to find.',
            '• Assignments can have a due date.',
            '• Your acknowledgment is timestamped and permanent.'
          ]
        }
      ]
    },
    {
      id: 'shift-report',
      title: 'Shift Report',
      icon: Activity,
      badge: 'End of Shift',
      content: [
        {
          title: 'End your shift the right way',
          description: 'The Shift Report is the handoff to the next shift. It composes activity, alerts, services, meals, bed counts, and free-text notes into a printable report.',
          steps: [
            '1. Open Shift Report from the sidebar at the end of your shift.',
            '2. Review the auto-populated sections.',
            '3. Add free-text notes for anything the system didn\'t capture.',
            '4. Submit. A supervisor can finalize.'
          ],
          callout: { type: 'tip', text: 'Print the shift report and leave a copy at the desk — it is designed for a clean print.' }
        }
      ]
    },
    {
      id: 'help',
      title: 'Getting Help (incl. Emergency Procedures & Troubleshooting)',
      icon: HelpCircle,
      badge: 'Support',
      content: [
        {
          title: 'Find anything with Cmd+K',
          description: 'Global search is the fastest way to jump to a client, staff member, facility, or screen. Press Cmd+K (Mac) or Ctrl+K (Windows) from anywhere in the app.'
        },
        {
          title: 'Contact support',
          description: 'For pilot support, reach out to your Cimfoni account contact. Include the facility name, what you were trying to do, and what happened instead.'
        },
        {
          title: 'Emergency — missing child',
          description: 'If a child cannot be located during a family check-in or bed count:',
          steps: [
            '1. Open the child\'s profile and start a **Missing Child Report** from the Alerts area.',
            '2. Set priority to **critical** — this triggers the escalation path.',
            '3. Follow your facility\'s in-person protocol immediately (do not wait for the system to notify).',
            '4. Update the report as the situation develops.'
          ],
          callout: { type: 'warning', text: 'The system is the record, not the response. Call your supervisor and local authorities first, then log.' }
        },
        {
          title: 'Emergency — evacuation',
          description: 'During an evacuation, use the printed census or the print action on the roster to walk out with a physical list of who was in the building.',
          callout: { type: 'tip', text: 'Print the census at the start of every shift so you always have a paper backup.' }
        },
        {
          title: 'Troubleshooting — can\'t sign in',
          steps: [
            '• Confirm you are using your work email.',
            '• Use "Forgot password" to reset — it works instantly.',
            '• If you were prompted to enroll MFA, make sure your authenticator app is set up.',
            '• If it still fails, ask your admin to check your account is active.'
          ]
        },
        {
          title: 'Troubleshooting — can\'t check a client in',
          steps: [
            '• Every check-in requires an assigned bed. Assign one first.',
            '• If the client is on an active pass, they cannot be checked in until the pass ends.',
            '• Confirm your role can perform check-ins (case managers, staff, supervisors, and admins can).'
          ]
        },
        {
          title: 'Troubleshooting — offline mode',
          description: 'If connectivity drops, Cimfoni keeps working with cached data and queues your changes.',
          steps: [
            '• An orange banner at the top confirms you are offline.',
            '• Continue checking clients in and out — changes are stored locally.',
            '• When you reconnect, changes sync automatically.'
          ]
        }
      ]
    },
    {
      id: 'advanced',
      title: 'Advanced Reference (Admins)',
      icon: Settings,
      badge: 'Admin',
      content: [
        {
          title: 'Staff & Facility Administration',
          description: 'Create staff, assign roles, deactivate/archive users, and configure facilities.',
          steps: [
            '• Staff creation issues a secure temporary password shown once in a copy-to-clipboard dialog.',
            '• Deactivation is a soft archive (is_active=false); history is preserved.',
            '• Super admins can soft-archive whole facilities.'
          ],
          screenshot: 'staff-management'
        },
        {
          title: 'Branding & System Settings',
          description: 'Super admins configure organization name, logo, login banner, and primary/secondary/accent colors from Settings → Branding. System settings control idle timeout, password complexity, and MFA requirements.',
          screenshot: 'admin-settings'
        },
        {
          title: 'Reporting & Export',
          description: 'CSV, Excel, and print outputs are available on client lists, service logs, census, activity, and shift reports. All exports honor your role and facility scope.'
        },
        {
          title: 'Security & Privacy',
          description: 'Every table has row-level security. Business rules are enforced server-side. Admins and managers must use MFA. The activity log records every material change.',
          callout: { type: 'note', text: 'Cimfoni does not accept or store protected health information (PHI). Allergies and emergency contacts are operational fields captured with consent.' }
        },
        {
          title: 'Multi-tenancy & Billing',
          description: 'Organizations own facilities and staff. Super admins manage subscriptions, module add-ons, and billing from the Billing tab.'
        },
        {
          title: 'Optional Modules',
          description: 'AI Assistant (currently offline for testing), Advanced Analytics, Inspections, and Training are optional paid modules that enable per organization from Billing.'
        },
        {
          title: 'Glossary',
          steps: [
            '• **Bed count** — structured presence verification at scheduled windows',
            '• **Case manager** — role that owns a caseload across facilities',
            '• **Family shelter** — facility flag enabling per-member check-in',
            '• **Module** — optional paid capability enabled per organization',
            '• **Organization** — the billing tenant; owns one or more facilities',
            '• **Pass** — time-boxed absence window that preserves a bed',
            '• **Roster** — live list of active clients at a facility today',
            '• **Shift report** — end-of-shift summary composed from activity',
            '• **Unbedded** — registered client without a current bed'
          ]
        }
      ]
    }
  ];

  // Filter out sections gated behind modules the org hasn't subscribed to.
  // Super Admins can override with "Show all (including disabled modules)".
  const visibleGuideContent = guideContent.filter(section => {
    const moduleKey = getModuleForGuideSection(section.id);
    if (!moduleKey) return true; // Core section — always visible.
    if (isSuperAdmin && showAllModules) return true;
    return hasModule(moduleKey as PaidModuleKey);
  });

  const generatePDF = async () => {
    setIsGeneratingPDF(true);
    try {
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 20;
      const contentWidth = pageWidth - (margin * 2);
      let yPosition = margin;

      const checkNewPage = (requiredSpace = 20) => {
        if (yPosition + requiredSpace > pageHeight - margin) {
          pdf.addPage();
          yPosition = margin;
          return true;
        }
        return false;
      };

      const addWrappedText = (text: string, x: number, y: number, maxWidth: number, fontSize: number) => {
        pdf.setFontSize(fontSize);
        const lines = pdf.splitTextToSize(text, maxWidth);
        pdf.text(lines, x, y);
        return lines.length * (fontSize * 0.35) + 2;
      };

      // Title Page
      pdf.setFontSize(28);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Shelter Management System', pageWidth / 2, 60, { align: 'center' });
      
      pdf.setFontSize(20);
      pdf.setFont('helvetica', 'normal');
      pdf.text('User Guide', pageWidth / 2, 80, { align: 'center' });
      
      pdf.setFontSize(14);
      pdf.text(`Generated: ${new Date().toLocaleDateString()}`, pageWidth / 2, 100, { align: 'center' });

      // Table of Contents
      pdf.addPage();
      yPosition = margin;
      pdf.setFontSize(22);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Table of Contents', margin, yPosition);
      yPosition += 20;

      visibleGuideContent.forEach((section, index) => {
        checkNewPage(10);
        pdf.setFontSize(14);
        pdf.setFont('helvetica', 'normal');
        pdf.text(`${index + 1}. ${section.title}`, margin + 5, yPosition);
        const pageNum = 3 + (index * 2);
        pdf.text(`${pageNum}`, pageWidth - margin - 20, yPosition);
        yPosition += 8;
      });

      // Process each section
      for (let sectionIndex = 0; sectionIndex < visibleGuideContent.length; sectionIndex++) {
        const section = visibleGuideContent[sectionIndex];
        pdf.addPage();
        yPosition = margin;
        
        pdf.setFontSize(20);
        pdf.setFont('helvetica', 'bold');
        pdf.text(`${sectionIndex + 1}. ${section.title}`, margin, yPosition);
        yPosition += 15;

        if (section.badge) {
          pdf.setFillColor(59, 130, 246);
          pdf.roundedRect(margin, yPosition, 30, 8, 2, 2, 'F');
          pdf.setTextColor(255, 255, 255);
          pdf.setFontSize(10);
          pdf.text(section.badge, margin + 15, yPosition + 5.5, { align: 'center' });
          pdf.setTextColor(0, 0, 0);
          yPosition += 15;
        }

        for (let contentIndex = 0; contentIndex < section.content.length; contentIndex++) {
          const content = section.content[contentIndex];
          
          checkNewPage(30);
          
          pdf.setFontSize(16);
          pdf.setFont('helvetica', 'bold');
          const titleHeight = addWrappedText(content.title, margin, yPosition, contentWidth, 16);
          yPosition += titleHeight + 5;

          if (content.description) {
            checkNewPage(20);
            pdf.setFont('helvetica', 'normal');
            const descHeight = addWrappedText(content.description, margin, yPosition, contentWidth, 11);
            yPosition += descHeight + 8;
          }

          if (content.steps) {
            checkNewPage(20);
            pdf.setFontSize(12);
            pdf.setFont('helvetica', 'bold');
            pdf.text('Steps:', margin, yPosition);
            yPosition += 8;

            content.steps.forEach((step: string, stepIndex: number) => {
              checkNewPage(15);
              pdf.setFont('helvetica', 'normal');
              pdf.setFontSize(10);
              
              const cleanStep = step.replace(/\*\*(.*?)\*\*/g, '$1').replace(/^\d+\.\s*/, '').replace(/^•\s*/, '');
              const stepNumber = step.match(/^\d+\./) ? '' : `${stepIndex + 1}. `;
              const fullStep = stepNumber + cleanStep;
              
              const stepHeight = addWrappedText(fullStep, margin + 5, yPosition, contentWidth - 10, 10);
              yPosition += stepHeight + 3;
            });
            yPosition += 5;
          }

          if (content.callout) {
            checkNewPage(25);
            
            const calloutColor = content.callout.type === 'warning' ? [255, 243, 205] : 
                               content.callout.type === 'tip' ? [219, 234, 254] : [243, 244, 246];
            pdf.setFillColor(calloutColor[0], calloutColor[1], calloutColor[2]);
            
            const calloutHeight = 20;
            pdf.roundedRect(margin, yPosition, contentWidth, calloutHeight, 3, 3, 'F');
            
            pdf.setFontSize(10);
            pdf.setFont('helvetica', 'bold');
            const calloutTitle = content.callout.type.charAt(0).toUpperCase() + content.callout.type.slice(1) + ':';
            pdf.text(calloutTitle, margin + 5, yPosition + 8);
            
            pdf.setFont('helvetica', 'normal');
            const textHeight = addWrappedText(content.callout.text, margin + 5, yPosition + 14, contentWidth - 10, 9);
            yPosition += Math.max(calloutHeight, textHeight + 10) + 5;
          }

          if ('screenshot' in content && content.screenshot) {
            const screenshots = Array.isArray(content.screenshot) ? content.screenshot : [content.screenshot];
            
            for (const screenshot of screenshots) {
              const screenshotSrc = getScreenshotSrc(screenshot);
              if (screenshotSrc) {
                try {
                  checkNewPage(80);
                  
                  const img = document.createElement('img');
                  img.src = screenshotSrc;
                  await new Promise<void>((resolve, reject) => {
                    img.onload = () => resolve();
                    img.onerror = reject;
                    setTimeout(reject, 5000);
                  });

                  const maxWidth = contentWidth - 20;
                  const maxHeight = 80;
                  const aspectRatio = img.width / img.height;
                  
                  let imgWidth = Math.min(maxWidth, img.width * 0.4);
                  let imgHeight = imgWidth / aspectRatio;
                  
                  if (imgHeight > maxHeight) {
                    imgHeight = maxHeight;
                    imgWidth = imgHeight * aspectRatio;
                  }

                  pdf.setFontSize(10);
                  pdf.setFont('helvetica', 'italic');
                  pdf.text(`Screenshot: ${screenshot}`, margin + 10, yPosition);
                  yPosition += 8;
                  
                  pdf.addImage(screenshotSrc, 'PNG', margin + 10, yPosition, imgWidth, imgHeight);
                  yPosition += imgHeight + 15;
                  
                } catch (error) {
                  console.warn('Failed to add screenshot:', error);
                  pdf.setFontSize(10);
                  pdf.setFont('helvetica', 'italic');
                  pdf.text('[Screenshot: ' + screenshot + ']', margin + 10, yPosition);
                  yPosition += 10;
                }
              }
            }
          }

          yPosition += 10;
        }
      }

      const totalPages = (pdf as any).internal.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        pdf.setPage(i);
        pdf.setFontSize(8);
        pdf.setFont('helvetica', 'normal');
        pdf.text(`Page ${i} of ${totalPages}`, pageWidth - margin, pageHeight - 10, { align: 'right' });
        pdf.text('Shelter Management System - User Guide', margin, pageHeight - 10);
      }

      pdf.save('shelter-management-guide.pdf');
      
    } catch (error) {
      console.error('Error generating PDF:', error);
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  return (
    <div className={cn("max-w-4xl mx-auto space-y-4", compact ? "p-4" : "p-6")}>
      {!compact && (
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-foreground mb-1">User Manual</h1>
            <p className="text-sm text-muted-foreground">
              Complete guide for using the Shelter Management System
            </p>
          </div>
          <Button onClick={generatePDF} disabled={isGeneratingPDF} className="flex items-center gap-2">
            <Download className="h-4 w-4" />
            {isGeneratingPDF ? 'Generating...' : 'Download PDF'}
          </Button>
        </div>
      )}

      {!compact && isSuperAdmin && (
        <div className="flex items-center justify-end gap-2 no-print">
          <Switch
            id="show-all-modules"
            checked={showAllModules}
            onCheckedChange={setShowAllModules}
          />
          <Label htmlFor="show-all-modules" className="text-xs text-muted-foreground">
            Show all (including modules this org hasn't subscribed to)
          </Label>
        </div>
      )}

      <div ref={printRef} className={cn("space-y-4", compact ? "text-sm" : "space-y-6")}>
        {!compact && (
          <div className="text-center space-y-3 no-print">
            <p className="text-muted-foreground max-w-2xl mx-auto text-sm">
              Follow these instructions to effectively use all features of the shelter management system.
            </p>
          </div>
        )}

        {visibleGuideContent.map(section => (
          <Card key={section.id} className="overflow-hidden">
            <CardHeader className={cn("pb-2", compact && "py-2")}>
              <Collapsible open={openSections.includes(section.id)} onOpenChange={() => toggleSection(section.id)}>
                <CollapsibleTrigger className="flex items-center justify-between w-full hover:text-primary transition-colors">
                  <div className="flex items-center gap-2">
                    <section.icon className={cn("text-primary", compact ? "h-4 w-4" : "h-5 w-5")} />
                    <CardTitle className={cn("font-semibold", compact ? "text-sm" : "text-lg")}>{section.title}</CardTitle>
                    <Badge variant="secondary" className={cn("ml-1", compact ? "text-xs px-1.5 py-0.5" : "text-xs")}>
                      {section.badge}
                    </Badge>
                  </div>
                  {openSections.includes(section.id) ? (
                    <ChevronDown className={cn(compact ? "h-3 w-3" : "h-4 w-4")} />
                  ) : (
                    <ChevronRight className={cn(compact ? "h-3 w-3" : "h-4 w-4")} />
                  )}
                </CollapsibleTrigger>

                <CollapsibleContent className={cn(compact ? "pt-2" : "pt-4")}>
                  <CardContent className={cn("pt-0", compact ? "space-y-3 pb-3" : "space-y-6")}>
                    {section.content.map((item, index) => (
                      <div key={index} className={cn(compact ? "space-y-2" : "space-y-4")}>
                        <div>
                          <h4 className={cn("font-semibold text-foreground mb-1", compact ? "text-sm" : "text-base")}>{item.title}</h4>
                          <p className={cn("text-muted-foreground mb-2", compact ? "text-xs" : "text-sm")}>{item.description}</p>
                          
                          {item.steps && (
                            <div className={cn(compact ? "space-y-1" : "space-y-2")}>
                              {item.steps.map((step, stepIndex) => (
                                <div key={stepIndex} className={cn(compact ? "text-xs" : "text-sm")}>
                                  {step.startsWith('- **') ? (
                                    <p className="text-muted-foreground leading-relaxed">
                                      <span className="font-semibold text-foreground">{step.match(/\*\*(.*?)\*\*/)?.[1]}</span>
                                      {step.replace(/^- \*\*.*?\*\*/, '')}
                                    </p>
                                  ) : step.startsWith('**') ? (
                                    <p className="text-muted-foreground leading-relaxed">
                                      <span className="font-semibold text-foreground">{step.match(/\*\*(.*?)\*\*/)?.[1]}</span>
                                      {step.replace(/\*\*.*?\*\*:?\s*/, '')}
                                    </p>
                                  ) : step.startsWith('•') ? (
                                    <div className="flex items-start gap-2">
                                      <span className="text-primary mt-1.5 text-xs">•</span>
                                      <span className="text-muted-foreground leading-relaxed">
                                        {step.replace(/^•\s*/, '').split(/\*\*(.*?)\*\*/).map((part, i) =>
                                          i % 2 === 1 ? <span key={i} className="font-semibold text-foreground">{part}</span> : part
                                        )}
                                      </span>
                                    </div>
                                  ) : step.trim() === '' ? (
                                    <div className="h-2"></div>
                                  ) : (
                                    <div className="flex items-start gap-2">
                                      {step.match(/^\d+\./) && (
                                        <span className={cn("inline-flex items-center justify-center rounded-full bg-primary/10 text-primary font-medium flex-shrink-0 mt-0.5", compact ? "w-4 h-4 text-xs" : "w-6 h-6 text-xs")}>
                                          {stepIndex + 1}
                                        </span>
                                      )}
                                      <span className="text-muted-foreground leading-relaxed">
                                        {step.replace(/^\d+\.\s*/, '').split(/\*\*(.*?)\*\*/).map((part, i) =>
                                          i % 2 === 1 ? <span key={i} className="font-semibold text-foreground">{part}</span> : part
                                        )}
                                      </span>
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}

                          {item.callout && (
                            <div className={cn(
                              "rounded-lg p-3 border-l-4 mt-3",
                              compact ? "text-xs" : "text-sm",
                              item.callout.type === 'tip' && "bg-blue-50 border-blue-400 text-blue-800 dark:bg-blue-950/30 dark:text-blue-200",
                              item.callout.type === 'warning' && "bg-yellow-50 border-yellow-400 text-yellow-800 dark:bg-yellow-950/30 dark:text-yellow-200",
                              item.callout.type === 'note' && "bg-green-50 border-green-400 text-green-800 dark:bg-green-950/30 dark:text-green-200"
                            )}>
                              <div className="flex items-start gap-2">
                                <span className="font-semibold capitalize">{item.callout.type}:</span>
                                <span>{item.callout.text}</span>
                              </div>
                            </div>
                          )}

                          {'screenshot' in item && item.screenshot && (
                            <div className={cn("mt-4 rounded-lg overflow-hidden border", compact ? "max-w-sm" : "max-w-2xl")}>
                              {(() => {
                                const src = getScreenshotSrc(item.screenshot as string);
                                return src ? (
                                  <img
                                    src={src}
                                    alt={item.title}
                                    className="w-full h-auto"
                                    loading="lazy"
                                  />
                                ) : null;
                              })()}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </CardContent>
                </CollapsibleContent>
              </Collapsible>
            </CardHeader>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default UserGuide;
